import { useId, useLayoutEffect, useRef, useState, type ChangeEvent, type ClipboardEvent } from "react";
import { codeStrays, findOneTimeCode, formatOneTimeCode, ONE_TIME_CODE_LENGTH } from "@vocifuchs/shared";
import { auth } from "../copy/auth.js";
import { inputClass } from "./ui.js";

const SEPARATORS = /[-\s]/g;

/** Where the caret goes in `code` to sit right after `chars` code characters. */
function caretAfter(code: string, chars: number): number {
  if (chars <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < code.length; i++) {
    if (code[i] !== "-") seen++;
    if (seen === chars) return i + 1;
  }
  return code.length;
}

/**
 * A one-time code (recovery, PIN reset, invites), formatted while typed or
 * pasted (issue #123). The caret stays where the person was typing; a
 * backspace or delete on a dash takes the character beyond it; a full code
 * takes no more characters. A pasted line keeps only the code in it. A
 * character no code holds is pointed out, not swallowed: «O» for «0» would
 * otherwise just vanish.
 */
export function OneTimeCodeField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (code: string) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);
  // Bumped with every edit: an edit that leaves the value as it was must still
  // put the caret back, after React has written the old value into the field.
  const [edits, setEdits] = useState(0);
  const strayId = useId();
  const strays = codeStrays(value);

  useLayoutEffect(() => {
    if (caret.current === null || document.activeElement !== input.current) return;
    input.current?.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  }, [value, edits]);

  const put = (next: string, at: number) => {
    caret.current = at;
    setEdits((n) => n + 1);
    onChange(next);
  };

  const change = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const kind = (e.nativeEvent as InputEvent).inputType;
    let chars = raw.replace(SEPARATORS, "");
    let before = raw.slice(0, e.target.selectionStart ?? raw.length).replace(SEPARATORS, "").length;
    const old = value.replace(SEPARATORS, "");
    if (chars === old && raw.length < value.length) {
      // Only a dash went: take the character beyond it instead.
      if (kind === "deleteContentBackward" && before > 0) {
        chars = chars.slice(0, before - 1) + chars.slice(before);
        before -= 1;
      } else if (kind === "deleteContentForward") {
        chars = chars.slice(0, before) + chars.slice(before + 1);
      }
    }
    if (chars.length > ONE_TIME_CODE_LENGTH) {
      // The code is full: keep it, rather than lose a character at its end.
      put(value, caretAfter(value, Math.max(0, before - (chars.length - old.length))));
      return;
    }
    const next = formatOneTimeCode(chars);
    put(next, caretAfter(next, before));
  };

  const paste = (e: ClipboardEvent<HTMLInputElement>) => {
    const found = findOneTimeCode(e.clipboardData.getData("text"));
    if (!found) return;
    e.preventDefault();
    put(found, found.length);
  };

  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-semibold text-ink-soft">{label}</span>
      <input
        ref={input}
        className={`${inputClass()} font-mono tracking-widest uppercase ${strays ? "border-rose!" : ""}`}
        value={value}
        onChange={change}
        onPaste={paste}
        placeholder="XXXX-XXXX-XXXX"
        autoCapitalize="characters"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        required
        aria-invalid={strays ? true : undefined}
        aria-describedby={strayId}
      />
      <span id={strayId} aria-live="polite" className="block text-sm">
        {strays ? (
          <span className="font-semibold text-rose">{auth.code.strays(strays)}</span>
        ) : hint ? (
          <span className="text-ink-soft">{hint}</span>
        ) : null}
      </span>
    </label>
  );
}
